// src/layouts/MainLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import hismaLogo from '../assets/img/hisma_logo_horizontal.png';

// Heroicons
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ChartBarIcon,
  UserIcon,
  UserGroupIcon,
  WrenchIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ArrowLeftOnRectangleIcon,
  BellIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  BuildingOfficeIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  TruckIcon,
  ClockIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  GiftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserCircleIcon,
  SwatchIcon,
  QrCodeIcon,
  UsersIcon,
  PhotoIcon,
  GlobeAltIcon,
  SunIcon,
  MoonIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

import { NotificationCenter } from '../components/superadmin/NotificationCenter';
import { ImpersonationBanner } from '../components/common/ImpersonationBanner';
import { hasPermission } from '../components/common/PrivateRoute';

// Componente de carga
const LoadingScreen = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
  </div>
);


interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  divider?: boolean;
  children?: MenuItem[];
}

const MainLayout: React.FC = () => {
  const { currentUser, userProfile, isImpersonating, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('hisma_sidebar_collapsed') === 'true';
  });
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('hisma_dark_mode') === 'true';
  });

  // Manejo de Modo Oscuro
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('hisma_dark_mode', String(darkMode));
  }, [darkMode]);

  // Manejo de Menú Colapsable
  const toggleSidebarCollapse = () => {
    const nextState = !sidebarCollapsed;
    setSidebarCollapsed(nextState);
    localStorage.setItem('hisma_sidebar_collapsed', String(nextState));
  };

  // Redirigir a login si no hay usuario autenticado
  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate, loading]);

  // Manejar cierre de sesión
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Opciones de menú para diferentes roles
  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];

    // ==================== MENÚ PARA SUPERADMIN (Solo si NO está en Modo Soporte) ====================
    if (userProfile?.role === 'superadmin' && !isImpersonating) {
      return [
        {
          text: 'Dashboard',
          icon: <HomeIcon className="w-5 h-5" />,
          path: '/dashboard',
          divider: false
        },
        {
          text: 'Gestión de Lubricentros',
          icon: <BuildingOfficeIcon className="w-5 h-5" />,

          path: '/superadmin/lubricentros',
          divider: false
        },
        {
          text: 'Todos los Servicios',
          icon: <WrenchScrewdriverIcon className="w-5 h-5" />,
          path: '/superadmin/servicios',
          divider: false
        },
        {
          text: 'Gestión de Planes',
          icon: <CreditCardIcon className="w-5 h-5" />,
          path: '/superadmin/planes',
          divider: false
        },
        {
          text: 'Gestión de Pagos',
          icon: <BanknotesIcon className="w-5 h-5" />,
          path: '/superadmin/pagos',
          divider: false
        },
        {
          text: 'Cupones de Descuento',
          icon: <GiftIcon className="w-5 h-5" />,
          path: '/superadmin/cupones',
          divider: false
        },
        {
          text: 'Distribuidores',
          icon: <TruckIcon className="w-5 h-5" />,
          path: '/superadmin/distribuidores',
          divider: false
        },
        {
          text: 'Estadísticas Globales',
          icon: <ChartBarIcon className="w-5 h-5" />,
          path: '/superadmin/reportes',
          divider: true
        },
        {
          text: 'Mi Perfil',
          icon: <UserIcon className="w-5 h-5" />,
          path: '/perfil',
          divider: false
        }
      ];
    }

    // ==================== MENÚ PARA DISTRIBUIDOR ====================
    if (userProfile?.role === 'distributor') {
      return [
        {
          text: 'Dashboard',
          icon: <HomeIcon className="w-5 h-5" />,
          path: '/distributor/dashboard',
          divider: false
        },
        {
          text: 'Mis Cupones',
          icon: <GiftIcon className="w-5 h-5" />,
          path: '/distributor/cupones',
          divider: true
        },
        {
          text: 'Configuración Perfil',
          icon: <UserIcon className="w-5 h-5" />,
          path: '/perfil',
          divider: false
        },
        {
          text: 'Soporte',
          icon: <QuestionMarkCircleIcon className="w-5 h-5" />,
          path: '/soporte',
          divider: false
        }
      ];
    }


    // ==================== MENÚ PARA USUARIOS NORMALES ====================
    items.push(
      {
        text: 'Dashboard',
        icon: <HomeIcon className="w-5 h-5" />,
        path: '/dashboard',
        divider: false
      }
    );

    // ==================== SECCIÓN CAMBIOS DE ACEITE ====================
    items.push(
      {
        text: 'Cambios de Aceite',
        icon: <WrenchIcon className="w-5 h-5" />,
        path: '/cambios-aceite',
        divider: false
      },
      {
        text: 'Precarga Rápida',
        icon: <PlusIcon className="w-5 h-5" />,
        path: '/cambios-aceite/precarga',
        divider: false
      },
      {
        text: 'Servicios Pendientes',
        icon: <ClockIcon className="w-5 h-5" />,
        path: '/cambios-aceite/pendientes',
        divider: false
      },
      {
        text: 'Próximos Servicios',
        icon: <CalendarIcon className="w-5 h-5" />,
        path: '/proximos-servicios',
        divider: true
      }
    );

    // ==================== SECCIÓN GARANTÍAS Y SERVICIOS ====================
    if (hasPermission(userProfile, 'garantias')) {
      items.push(
        {
          text: 'Garantías',
          icon: <ShieldCheckIcon className="w-5 h-5" />,
          path: '/garantias',
          divider: true
        }
      );
    }

    // ==================== SECCIÓN CLIENTES ====================
    if (hasPermission(userProfile, 'clientes')) {
      items.push(
        {
          text: 'Clientes',
          icon: <UserGroupIcon className="w-5 h-5" />,
          path: '/clientes',
          divider: true
        }
      );
    }

    // ==================== SECCIÓN ADMINISTRATIVA ====================
    if (userProfile?.role === 'admin') {
      items.push(
        {
          text: 'Reportes',
          icon: <ChartBarIcon className="w-5 h-5" />,
          path: '/reportes',
          divider: true
        }
      );
    }

    // ==================== SECCIÓN CONFIGURACIÓN (SUBMENÚ SOLO PARA ADMIN) ====================
    if (userProfile?.role === 'admin') {
      items.push(
        {
          text: 'Configuración',
          icon: <Cog6ToothIcon className="w-5 h-5" />,
          path: '/settings',
          divider: false,
          children: [
            { text: 'Datos del Lubricentro', path: '/settings/lubricentro', icon: <BuildingStorefrontIcon className="w-5 h-5" /> },
            { text: 'Usuarios', path: '/settings/users', icon: <UsersIcon className="w-5 h-5" /> },
            { text: 'Portal del Cliente', path: '/settings/portal', icon: <GlobeAltIcon className="w-5 h-5" /> },
            { text: 'Apariencia', path: '/settings/theme', icon: <SwatchIcon className="w-5 h-5" /> },
            { text: 'Etiquetas y QR', path: '/settings/qr', icon: <QrCodeIcon className="w-5 h-5" /> },
            { text: 'Tarjeta de Servicio', path: '/settings/card', icon: <PhotoIcon className="w-5 h-5" /> },
            { text: 'Suscripción y Pagos', path: '/admin/pagos', icon: <CreditCardIcon className="w-5 h-5" /> },
          ]
        }
      );
    }

    items.push(
      {
        text: 'Soporte',
        icon: <QuestionMarkCircleIcon className="w-5 h-5" />,
        path: '/soporte',
        divider: false
      }
    );

    return items;
  };



  // Auto-expandir menús si una ruta hija está activa
  useEffect(() => {
    const menuItems = getMenuItems();
    const newExpandedMenus = { ...expandedMenus };
    let hasChanges = false;

    menuItems.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child => location.pathname.startsWith(child.path));
        if (isChildActive && !newExpandedMenus[item.path]) {
          newExpandedMenus[item.path] = true;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setExpandedMenus(newExpandedMenus);
    }
  }, [location.pathname, userProfile, isImpersonating]); // Regenerar menú al cambiar ruta, perfil o modo soporte


  // No renderizar nada hasta que se confirme la autenticación
  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser || !userProfile) {
    return null;
  }

  const menuItems = getMenuItems();

  const getUserInitials = () => {
    if (!userProfile) return 'U';
    return `${userProfile.nombre.charAt(0)}${userProfile.apellido.charAt(0)}`;
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Administrador';
      case 'owner': return 'Propietario';
      case 'distributor': return 'Distribuidor';
      case 'user': return 'Empleado';
      default: return 'Usuario';
    }

  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = (item: MenuItem, isMobile: boolean = false) => {
    const isCollapsedDesktop = !isMobile && sidebarCollapsed;

    if (item.children) {
      const isExpanded = expandedMenus[item.path];
      const isParentActive = item.children.some(child => isActive(child.path));

      return (
        <div key={item.path} className="space-y-1">
          <button
            onClick={() => toggleMenu(item.path)}
            title={isCollapsedDesktop ? item.text : undefined}
            className={`w-full group flex items-center ${isCollapsedDesktop ? 'justify-center px-2' : 'justify-between px-3'} py-2.5 text-base font-medium rounded-xl transition-all duration-150
              ${isParentActive ? 'bg-primary-800 text-white shadow-sm' : 'text-white hover:bg-primary-600'}
              ${isMobile ? 'text-base' : 'text-sm'}
            `}
          >
            <div className="flex items-center">
              <div className={isCollapsedDesktop ? 'flex-shrink-0' : 'mr-3 flex-shrink-0'}>{item.icon}</div>
              {!isCollapsedDesktop && <span className="truncate">{item.text}</span>}
            </div>
            {!isCollapsedDesktop && (
              isExpanded ? (
                <ChevronUpIcon className="w-4 h-4 ml-2" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 ml-2" />
              )
            )}
          </button>

          {isExpanded && !isCollapsedDesktop && (
            <div className="space-y-1 pl-9">
              {item.children.map(child => (
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={({ isActive }) => `
                    group flex items-center px-2.5 py-2 font-medium rounded-lg transition-colors duration-150
                    ${isActive ? 'bg-primary-900 text-white shadow-xs' : 'text-primary-100 hover:bg-primary-600 hover:text-white'}
                    ${isMobile ? 'text-sm' : 'text-xs'}
                  `}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <div className="mr-2.5 flex-shrink-0">{child.icon}</div>
                  <span className="truncate">{child.text}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        title={isCollapsedDesktop ? item.text : undefined}
        className={`group flex items-center ${isCollapsedDesktop ? 'justify-center px-2' : 'px-3'} py-2.5 font-medium rounded-xl transition-all duration-150 ${isActive(item.path)
          ? 'bg-primary-800 text-white shadow-md'
          : 'text-white hover:bg-primary-600 hover:text-white'
          } ${isMobile ? 'text-base' : 'text-sm'}`}
        onClick={() => isMobile && setSidebarOpen(false)}
      >
        <div className={isCollapsedDesktop ? 'flex-shrink-0' : 'mr-3 flex-shrink-0'}>{item.icon}</div>
        {!isCollapsedDesktop && <span className="truncate">{item.text}</span>}
      </NavLink>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 dark:bg-slate-950 text-gray-900 dark:text-slate-100">
      {/* ==================== SIDEBAR MÓVIL ==================== */}
      <div className={`fixed inset-0 z-40 flex md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity duration-300 ease-linear
                      ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setSidebarOpen(false)}
        ></div>

        <div
          className={`relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-primary-700 transition duration-300 ease-in-out transform
                      ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <div className="absolute top-0 right-0 pt-2 pr-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-shrink-0 flex items-center px-4">
            <img src={hismaLogo} alt="HISMA" className="h-8" />
          </div>

          <div className="mt-5 flex-1 h-0 overflow-y-auto">
            <nav className="px-2 space-y-1">
              {menuItems.map((item) => (
                <React.Fragment key={item.path}>
                  {renderMenuItem(item, true)}
                  {item.divider && <hr className="border-t border-primary-600 my-2" />}
                </React.Fragment>
              ))}
            </nav>
          </div>

          <div className="flex-shrink-0 flex border-t border-primary-800 p-4">
            <div className="flex-shrink-0 w-full group block">
              <div className="flex items-center">
                <div>
                  {userProfile.photoURL ? (
                    <img
                      className="inline-block h-10 w-10 rounded-full"
                      src={userProfile.photoURL}
                      alt={`${userProfile.nombre} ${userProfile.apellido}`}
                    />
                  ) : (
                    <div className="inline-flex h-10 w-10 rounded-full bg-primary-600 text-white items-center justify-center">
                      {getUserInitials()}
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-base font-medium text-white">
                    {`${userProfile.nombre} ${userProfile.apellido}`}
                  </p>
                  <p className="text-sm font-medium text-primary-300">
                    {getRoleName(userProfile.role)}
                  </p>
                  {(userProfile.role === 'admin' || userProfile.role === 'user') && userProfile.lubricentroId && (
                    <p className="text-xs text-primary-200 mt-1">
                      {userProfile.fantasyName || 'Lubricentro'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SIDEBAR DESKTOP COLAPSABLE ==================== */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className={`flex flex-col transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
          <div className="flex flex-col h-0 flex-1">
            {/* Header del Sidebar con botón de colapso */}
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'} h-16 flex-shrink-0 bg-primary-800 border-b border-primary-900/50 transition-all duration-300`}>
              {!sidebarCollapsed && (
                <img src={hismaLogo} alt="HISMA" className="h-8 transition-all" />
              )}
              <button
                type="button"
                onClick={toggleSidebarCollapse}
                className="p-1.5 rounded-xl text-primary-200 hover:text-white hover:bg-primary-700 transition-colors cursor-pointer"
                title={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
              >
                {sidebarCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5" />
                ) : (
                  <ChevronLeftIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="flex-1 flex flex-col overflow-y-auto bg-primary-700">
              <nav className="flex-1 px-2 py-4 space-y-1">
                {menuItems.map((item) => (
                  <React.Fragment key={item.path}>
                    {renderMenuItem(item, false)}
                    {item.divider && <hr className="border-t border-primary-600 my-3" />}
                  </React.Fragment>
                ))}
              </nav>

              <div className="p-3 border-t border-primary-600">
                <button
                  onClick={handleLogout}
                  title={sidebarCollapsed ? 'Cerrar Sesión' : undefined}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-2 text-sm font-medium rounded-xl text-white hover:bg-primary-600 transition-colors duration-150`}
                >
                  <ArrowLeftOnRectangleIcon className={`${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0 h-5 w-5`} />
                  {!sidebarCollapsed && <span className="truncate">Cerrar Sesión</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== CONTENIDO PRINCIPAL ==================== */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Banner de Modo Soporte si está activo */}
        <ImpersonationBanner />

        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white dark:bg-slate-900 shadow-sm border-b border-gray-200 dark:border-slate-800 transition-colors">

          <button
            className="px-4 border-r border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {isImpersonating ? (
                  <span className="flex items-center text-amber-700 dark:text-amber-400 font-bold">
                    Panel del Lubricentro • {userProfile?.fantasyName || 'Modo Soporte'}
                  </span>
                ) : userProfile?.lubricentroId && userProfile.role !== 'superadmin' ? (
                  <span>Panel de Control</span>
                ) : userProfile?.role === 'superadmin' ? (
                  <span>Panel de Administración</span>
                ) : (
                  <span>Panel de Control</span>
                )}
              </h1>
            </div>


            <div className="ml-4 flex items-center md:ml-6 gap-1">
              {userProfile.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin/pagos')}
                  className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800 rounded-xl transition-colors duration-150"
                  title="Gestión de Pagos"
                >
                  <CreditCardIcon className="h-5 w-5" />
                </button>
              )}

              {userProfile.role === 'superadmin' && (
                <button
                  onClick={() => navigate('/superadmin/pagos-transferencias')}
                  className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-slate-800 rounded-xl transition-colors duration-150"
                  title="Gestión de Transferencias"
                >
                  <BanknotesIcon className="h-5 w-5" />
                </button>
              )}

              {/* Botón de Modo Oscuro / Claro */}
              <button
                type="button"
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-xl text-gray-500 dark:text-slate-300 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors focus:outline-none"
                title={darkMode ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'}
              >
                {darkMode ? (
                  <SunIcon className="h-5 w-5 text-yellow-400" />
                ) : (
                  <MoonIcon className="h-5 w-5 text-slate-600" />
                )}
              </button>

              {/* Centro de Notificaciones (para todos los usuarios) */}
              <div className="mr-1">
                <NotificationCenter />
              </div>


              <div className="ml-3 relative">
                <div>
                  <button
                    className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-150"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                  >
                    {userProfile.photoURL ? (
                      <img
                        className="h-8 w-8 rounded-full"
                        src={userProfile.photoURL}
                        alt={`${userProfile.nombre} ${userProfile.apellido}`}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">
                        {getUserInitials()}
                      </div>
                    )}
                  </button>
                </div>

                {userMenuOpen && (
                  <div
                    className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <div className="block px-4 py-3 text-sm border-b border-gray-100">
                      <div className="font-medium text-gray-900">
                        {`${userProfile.nombre} ${userProfile.apellido}`}
                      </div>
                      <div className="text-primary-600 font-medium">
                        {getRoleName(userProfile.role)}
                      </div>
                      {(userProfile.role === 'admin' || userProfile.role === 'user') && userProfile.lubricentroId && (
                        <div className="text-gray-500 text-xs mt-1">
                          {userProfile.fantasyName || 'Lubricentro'}
                        </div>
                      )}
                    </div>

                    <NavLink
                      to="/perfil"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                    >
                      <UserIcon className="inline h-4 w-4 mr-2" />
                      Mi Perfil
                    </NavLink>

                    {userProfile.role === 'admin' && (
                      <NavLink
                        to="/admin/pagos"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                      >
                        <CreditCardIcon className="inline h-4 w-4 mr-2" />
                        Gestión de Pagos
                      </NavLink>
                    )}

                    {userProfile.role === 'superadmin' && (
                      <NavLink
                        to="/superadmin/pagos-transferencias"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                      >
                        <BanknotesIcon className="inline h-4 w-4 mr-2" />
                        Pagos por Transferencia
                      </NavLink>
                    )}

                    <NavLink
                      to="/soporte"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                    >
                      <QuestionMarkCircleIcon className="inline h-4 w-4 mr-2" />
                      Soporte
                    </NavLink>

                    <div className="border-t border-gray-100"></div>

                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                    >
                      <ArrowLeftOnRectangleIcon className="inline h-4 w-4 mr-2" />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;